import { Injectable } from '@angular/core'
import { Validators, AbstractControl, ValidationErrors } from '@angular/forms'

@Injectable({
  providedIn: 'root',
})
export class FormValidationService {
  // validators for RBAC forms
  /**
   * External ID validation pattern - allows letters, numbers, hyphens, and underscores
   */
  static readonly EXTERNAL_ID_PATTERN = '^[a-zA-Z0-9_-]*$'

  /**
   * External ID maximum length
   */
  static readonly EXTERNAL_ID_MAX_LENGTH = 64

  /**
   * Get validators for external ID field
   * @returns Array of Angular validators for external ID
   */
  getExternalIdValidators() {
    return [
      Validators.pattern(FormValidationService.EXTERNAL_ID_PATTERN),
      Validators.maxLength(FormValidationService.EXTERNAL_ID_MAX_LENGTH),
    ]
  }

  /**
   * Get error message for external ID validation errors
   * @param control The form control to check for errors
   * @returns Error message string or null if no errors
   */
  getExternalIdErrors(control: AbstractControl | null): string | null {
    if (!control?.errors || !control?.touched) {
      return null
    }

    if (control.errors['pattern']) {
      return 'External ID can only contain letters, numbers, hyphens, and underscores'
    }

    if (control.errors['maxlength']) {
      return `External ID cannot exceed ${FormValidationService.EXTERNAL_ID_MAX_LENGTH} characters`
    }

    return null
  }

  /**
   * Convenience method to get external ID errors from a form control by name
   * @param form The form group containing the external ID control
   * @param controlName The name of the external ID control (default: 'externalId')
   * @returns Error message string or null if no errors
   */
  getExternalIdErrorsFromForm(form: any, controlName: string = 'externalId'): string | null {
    const control = form?.get(controlName)
    return this.getExternalIdErrors(control)
  }

  //end RBAC forms

  // validators for create account form

  /**
   * Sets up watchers on account ID form controls to automatically clear the target region
   * when same-account same-region configuration is detected (which is invalid).
   *
   * @param formGroup The form group containing the account and region controls
   * @param sanitizeAccountId Function to sanitize account IDs for comparison
   * @param sanitizeRegion Function to sanitize region names for comparison
   */
  setupSameAccountSameRegionValidation(
    formGroup: any,
    sanitizeAccountId: (id: string) => string,
    sanitizeRegion: (region: string) => string
  ) {
    const validateAndResetTargetRegion = () => {
      const sourceAccount = sanitizeAccountId(
        formGroup.value.sourceAccountId || formGroup.value.sourceAwsAccountId || ''
      )
      const targetAccount = sanitizeAccountId(
        formGroup.value.targetAccountId || formGroup.value.targetAwsAccountId || ''
      )
      const sourceRegion = sanitizeRegion(formGroup.value.sourceRegion || '')
      const targetRegion = sanitizeRegion(formGroup.value.targetRegion || '')

      // Check if we have same account + same region (invalid configuration)
      if (sourceAccount && targetAccount && sourceRegion && targetRegion) {
        const isSameAccount = sourceAccount === targetAccount
        const isSameRegion = sourceRegion === targetRegion

        if (isSameAccount && isSameRegion) {
          // Same account + same region is never allowed - reset target region
          formGroup.patchValue({ targetRegion: '' })
        }
      }
    }

    // Watch for account ID changes and reset target region if configuration becomes invalid
    const sourceAccountControl = formGroup.get('sourceAccountId') || formGroup.get('sourceAwsAccountId')
    const targetAccountControl = formGroup.get('targetAccountId') || formGroup.get('targetAwsAccountId')

    sourceAccountControl?.valueChanges.subscribe(() => {
      validateAndResetTargetRegion()
    })
    targetAccountControl?.valueChanges.subscribe(() => {
      validateAndResetTargetRegion()
    })
  }
}
